import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { trackEvent } from '../../../hooks/useGoogleAnalytics';

const AuthorizePaymentModal = ({ payment, onClose, onAuthorize, currentLanguage, loading }) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');

  const translations = {
    en: {
      authorizePayment: 'Authorize Payment',
      paymentDetails: 'Payment Details',
      amount: 'Amount',
      cardInformation: 'Card Information',
      cardNumber: 'Card Number',
      expiryDate: 'Expiry Date',
      cvv: 'CVV',
      cardholderName: 'Cardholder Name',
      cancel: 'Cancel',
      authorize: 'Authorize Payment',
      securePayment: 'Your payment is secured with 256-bit encryption',
      escrowNotice: 'Funds will be held in escrow until delivery is confirmed'
    },
    ko: {
      authorizePayment: '결제 승인',
      paymentDetails: '결제 세부정보',
      amount: '금액',
      cardInformation: '카드 정보',
      cardNumber: '카드 번호',
      expiryDate: '만료일',
      cvv: 'CVV',
      cardholderName: '카드 소유자 이름',
      cancel: '취소',
      authorize: '결제 승인',
      securePayment: '귀하의 결제는 256비트 암호화로 보호됩니다',
      escrowNotice: '배송이 확인될 때까지 자금이 에스크로에 보관됩니다'
    }
  };

  const t = translations?.[currentLanguage];

  const formatCurrency = (amount) => {
    return currentLanguage === 'ko' 
      ? `₩${amount?.toLocaleString('ko-KR')}`
      : `$${amount?.toLocaleString('en-US')}`;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Track payment authorization event
    trackEvent('payment_authorization', {
      deal_id: payment?.id,
      payment_method: 'card',
      page: '/payment-screen'
    });
    
    onAuthorize({
      cardNumber,
      expiryDate,
      cvv,
      cardholderName
    });
  };

  const isFormValid = cardNumber && expiryDate && cvv && cardholderName;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-200 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border shadow-elevation-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              {t?.authorizePayment}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-base"
              disabled={loading}
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="mb-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">{t?.amount}</span>
              <span className="text-2xl font-bold text-primary">{formatCurrency(payment?.amount)}</span>
            </div>
            <div className="flex items-start gap-2 mt-3">
              <Icon name="Info" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">{t?.escrowNotice}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">{t?.cardInformation}</h4>
              <div className="space-y-4">
                <Input
                  label={t?.cardNumber}
                  type="text"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e?.target?.value)}
                  placeholder="1234 5678 9012 3456"
                  required
                  disabled={loading}
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label={t?.expiryDate}
                    type="text"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e?.target?.value)}
                    placeholder="MM/YY"
                    required
                    disabled={loading}
                  />
                  <Input
                    label={t?.cvv}
                    type="text"
                    value={cvv}
                    onChange={(e) => setCvv(e?.target?.value)}
                    placeholder="123"
                    required
                    disabled={loading}
                  />
                </div>

                <Input
                  label={t?.cardholderName}
                  type="text"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e?.target?.value)}
                  placeholder="John Doe"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg border border-success/20">
              <Icon name="Lock" size={16} color="var(--color-success)" />
              <p className="text-xs text-muted-foreground">{t?.securePayment}</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                size="lg"
                fullWidth
                onClick={onClose}
                disabled={loading}
              >
                {t?.cancel}
              </Button>
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                iconName="CreditCard"
                iconPosition="left"
                disabled={!isFormValid || loading}
                loading={loading}
              >
                {t?.authorize}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthorizePaymentModal;